/**
 * Reads EXIF orientation from image file and returns corrected + compressed image as data URL
 * @param file - The image file to process
 * @param maxDimension - Maximum width/height (default 1280px for faster uploads)
 * @param quality - JPEG quality 0-1 (default 0.7 for good compression)
 */
export async function getOrientedImage(
  file: File, 
  maxDimension: number = 1280, 
  quality: number = 0.7
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Get EXIF orientation
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Read orientation from EXIF data
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const orientation = getOrientation(new Uint8Array(arrayBuffer));
        
        // Calculate scaled dimensions
        let width = img.width;
        let height = img.height;
        
        // Scale down if needed
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        // Set canvas dimensions based on orientation
        if (orientation > 4) {
          canvas.width = height;
          canvas.height = width;
        } else {
          canvas.width = width;
          canvas.height = height;
        }

        // Transform canvas based on EXIF orientation
        switch (orientation) {
          case 2:
            ctx.transform(-1, 0, 0, 1, width, 0);
            break;
          case 3:
            ctx.transform(-1, 0, 0, -1, width, height);
            break;
          case 4:
            ctx.transform(1, 0, 0, -1, 0, height);
            break;
          case 5:
            ctx.transform(0, 1, 1, 0, 0, 0);
            break;
          case 6:
            ctx.transform(0, 1, -1, 0, height, 0);
            break;
          case 7:
            ctx.transform(0, -1, -1, 0, height, width);
            break;
          case 8:
            ctx.transform(0, -1, 1, 0, 0, width);
            break;
        }

        // Draw scaled image on canvas with correct orientation
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert canvas to compressed data URL
        resolve(canvas.toDataURL('image/jpeg', quality));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Extract orientation value from EXIF data
 */
function getOrientation(buffer: Uint8Array): number {
  const view = new DataView(buffer.buffer);
  
  // Check for JPEG signature
  if (view.getUint16(0, false) !== 0xFFD8) {
    return 1; // Default orientation
  }

  const length = view.byteLength;
  let offset = 2;

  while (offset < length) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    // Check for APP1 marker (EXIF data)
    if (marker === 0xFFE1) {
      const exifLength = view.getUint16(offset, false);
      offset += 2;

      // Check for EXIF signature
      const exifSignature = view.getUint32(offset, false);
      if (exifSignature !== 0x45786966) {
        return 1;
      }

      offset += 6; // Skip EXIF signature and padding

      // Check byte order
      const byteOrder = view.getUint16(offset, false);
      const littleEndian = byteOrder === 0x4949;
      offset += 2;

      // Skip fixed value
      offset += 2;

      // Get offset to IFD
      const ifdOffset = view.getUint32(offset, littleEndian);
      offset += ifdOffset - 8;

      // Get number of directory entries
      const tags = view.getUint16(offset, littleEndian);
      offset += 2;

      // Search for orientation tag (0x0112)
      for (let i = 0; i < tags; i++) {
        const tag = view.getUint16(offset + i * 12, littleEndian);
        if (tag === 0x0112) {
          return view.getUint16(offset + i * 12 + 8, littleEndian);
        }
      }
    } else if ((marker & 0xFF00) !== 0xFF00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }

  return 1; // Default orientation
}
